"use client";

import { getBaseApiClient } from "@newxsystems/api-base/client";
import { getBlogPostsPageQueryKey } from "@newxsystems/api-base/keys";
import { cn } from "@newxsystems/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { parseAsInteger, useQueryState } from "nuqs";
import { useEffect, useMemo, useRef, useState } from "react";
import { TootBlogPostCard } from "../../../../modules/content/components/TootBlogPostCard/TootBlogPostCard";
import { getTootSectionSpacing } from "../../../../modules/layout/toot-section-spacing";
import { RichTextHtml } from "../../RichTextHtml";
import type { IBlogListingBlockProps } from "./BlogListingBlock.props";

const getPaginationItems = (current: number, total: number) =>
  Array.from(new Set([1, total, current - 1, current, current + 1]))
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);
const buttonClass =
  "inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-full border border-toot-grid-strong bg-card px-4 text-sm font-black text-toot-foreground transition-colors hover:border-toot-orange hover:text-toot-orange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-45";
export const BlogListingBlockClient = ({
  anchorId,
  emptyText,
  eyebrow,
  heading,
  pageSize,
  parentClass,
  spacing,
  subheading,
}: IBlogListingBlockProps) => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const [pageQuery, setPageQuery] = useQueryState(
    "page",
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
      history: "push",
      scroll: false,
      shallow: true,
    }),
  );
  const currentPage = Math.max(pageQuery, 1);
  const { data, isFetching, isPlaceholderData } = useQuery({
    queryKey: getBlogPostsPageQueryKey(currentPage, pageSize),
    queryFn: async () => {
      const client = getBaseApiClient();
      if (!client) throw new Error("Base API client is not available.");
      return client.blog.list({ page: currentPage, pageSize });
    },
    placeholderData: keepPreviousData,
  });
  const posts = data?.items ?? [];
  const featuredPosts = (data?.page ?? currentPage) === 1 ? (data?.featuredPosts ?? []) : [];
  const totalPages = data?.totalPages ?? 1;
  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  useEffect(() => {
    if (pendingPage == null || isFetching || isPlaceholderData || data?.page !== pendingPage)
      return;
    setPendingPage(null);
    window.requestAnimationFrame(() =>
      sectionRef.current?.scrollIntoView({ behavior: "auto", block: "start" }),
    );
  }, [data?.page, isFetching, isPlaceholderData, pendingPage]);

  const selectPage = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage || isFetching) return;
    setPendingPage(page);
    void setPageQuery(page);
  };

  const [leadFeatured, ...secondaryFeatured] = featuredPosts.slice(0, 3);
  const headingId = heading && anchorId ? `${anchorId}-heading` : undefined;

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative overflow-hidden text-toot-foreground",
        getTootSectionSpacing(spacing),
        parentClass,
      )}
      id={anchorId ?? undefined}
    >
      <div className="toot-container relative scroll-mt-6">
        <div ref={sectionRef} />
        {eyebrow || heading || subheading ? (
          <header className="mb-10 max-w-4xl md:mb-14">
            {eyebrow ? <p className="toot-eyebrow mb-4">{eyebrow}</p> : null}
            {heading ? (
              <h2 className="toot-heading-section" id={headingId}>
                {heading}
              </h2>
            ) : null}
            {subheading ? (
              <RichTextHtml className="toot-subheading mt-5 max-w-2xl" html={subheading} />
            ) : null}
          </header>
        ) : null}

        {leadFeatured ? (
          <div className="mb-10 lg:relative lg:left-1/2 lg:w-screen lg:-translate-x-1/2 lg:px-6 xl:px-8">
            <div className="mx-auto w-full lg:max-w-[88rem] xl:max-w-[98rem]">
              <div
                className={cn(
                  "grid gap-5 lg:gap-6",
                  secondaryFeatured.length > 0 &&
                    "lg:grid-cols-[minmax(0,1.55fr)_minmax(19rem,0.85fr)] lg:grid-rows-2",
                )}
              >
                <TootBlogPostCard
                  className={cn(
                    "min-h-[28rem] lg:min-h-[40rem]",
                    secondaryFeatured.length > 0 && "lg:row-span-2",
                  )}
                  post={leadFeatured}
                  priority
                  variant="featured"
                />
                {secondaryFeatured.map((post) => (
                  <TootBlogPostCard
                    className="min-h-[22rem]"
                    key={post.id}
                    post={post}
                    variant="featuredCompact"
                  />
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {posts.length ? (
          <ul
            aria-busy={isFetching}
            className="grid list-none gap-5 p-0 md:grid-cols-2 lg:gap-6 xl:grid-cols-3"
          >
            {posts.map((post, index) => (
              <li className="min-w-0" key={post.id}>
                <TootBlogPostCard post={post} priority={index < 3 && currentPage === 1} />
              </li>
            ))}
          </ul>
        ) : !leadFeatured && emptyText ? (
          <p className="rounded-[var(--toot-radius-card)] border border-dashed border-toot-grid-strong bg-card p-6 text-toot-muted">
            {emptyText}
          </p>
        ) : null}

        {totalPages > 1 ? (
          <nav
            aria-label="Blog posts pagination"
            className="mt-10 flex flex-wrap items-center justify-center gap-2 lg:mt-12"
          >
            <button
              className={buttonClass}
              disabled={currentPage <= 1 || isFetching}
              onClick={() => selectPage(currentPage - 1)}
              type="button"
            >
              Previous
            </button>
            {paginationItems.map((pageNumber, index) => {
              const showGap = index > 0 && pageNumber - paginationItems[index - 1]! > 1;
              const current = pageNumber === currentPage;
              return (
                <span className="inline-flex items-center gap-2" key={pageNumber}>
                  {showGap ? (
                    <span aria-hidden="true" className="px-1 text-toot-muted">
                      …
                    </span>
                  ) : null}
                  <button
                    aria-current={current ? "page" : undefined}
                    aria-label={`Page ${pageNumber}${current ? ", current page" : ""}`}
                    className={cn(
                      buttonClass,
                      current && "border-toot-red bg-toot-red text-white hover:text-white",
                    )}
                    disabled={current || isFetching}
                    onClick={() => selectPage(pageNumber)}
                    type="button"
                  >
                    {pendingPage === pageNumber && isFetching ? (
                      <span className="sr-only">Loading </span>
                    ) : null}
                    {pageNumber}
                  </button>
                </span>
              );
            })}
            <button
              className={buttonClass}
              disabled={currentPage >= totalPages || isFetching}
              onClick={() => selectPage(currentPage + 1)}
              type="button"
            >
              Next
            </button>
          </nav>
        ) : null}
      </div>
    </section>
  );
};

BlogListingBlockClient.displayName = "Block::BlogListingBlockClient";
